import { Grid, Skeleton } from "@chakra-ui/react";
import { FormattedMessage } from "react-intl";

import { ProcessRead } from "@nens/rana/api-v1";
import { RanaButton, RanaHeading, RightArrowIcon, SIZES } from "@nens/ranalib";

import { LargeTileCard } from "@/components/large-tile-card";
import { useProcessListQuery } from "@/queries/query-process";
import { getProcessImageSrc } from "@/utils/get-process-image-src";

import { ProcessLibraryCard } from "./process-library-card";

interface ProcessListProps {
  setProcess: (process: ProcessRead) => void;
}

const GRID_ITEM_HEIGHT = SIZES.xs;

export function ProcessList({ setProcess }: ProcessListProps) {
  const processListQuery = useProcessListQuery();
  const { isLoading, data } = processListQuery;

  const processes = data?.items ?? [];
  // In the backend ordered so the first 5 processes are the default ones
  const defaultProcesses = processes.slice(0, 5);
  const otherProcesses = processes.slice(5);

  return (
    <Grid
      templateColumns={{ base: "repeat(1, 1fr)", md: "repeat(2, 1fr)", xl: "repeat(3, 1fr)" }}
      gap={3}
      mb={3}
    >
      {isLoading ? (
        <>
          <Skeleton h={GRID_ITEM_HEIGHT} />
          <Skeleton h={GRID_ITEM_HEIGHT} />
          <Skeleton h={GRID_ITEM_HEIGHT} />
          <Skeleton h={GRID_ITEM_HEIGHT} />
          <Skeleton h={GRID_ITEM_HEIGHT} />
          <Skeleton h={GRID_ITEM_HEIGHT} />
        </>
      ) : (
        <>
          {defaultProcesses.map((process) => {
            return (
              <LargeTileCard
                key={process.name}
                title={<RanaHeading type="h4">{process.name}</RanaHeading>}
                description={process.shortDescription}
                height={GRID_ITEM_HEIGHT}
                imageSrc={getProcessImageSrc(process.tags)}
                imageAlt={process.name ?? ""}
                footer={
                  <RanaButton
                    as="div"
                    ml="auto"
                    mr={4}
                    mb={4}
                    mt={2}
                    flexShrink={0}
                    h="fit"
                    variant="fourthiary"
                    rightIcon={<RightArrowIcon />}
                    color="brand.analytic"
                  >
                    <FormattedMessage id="processlist.use_process" defaultMessage="Use process" />
                  </RanaButton>
                }
                onClick={() => setProcess(process)}
              />
            );
          })}
          {otherProcesses.length > 0 && (
            <ProcessLibraryCard processes={otherProcesses} setProcess={setProcess} />
          )}
        </>
      )}
    </Grid>
  );
}
